import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex items-center gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-white/10 animate-pulse rounded" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.filter(item => item.label !== "Über Uns2").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative text-sm font-medium uppercase tracking-[0.1em] text-white/90 transition-colors hover:text-white">
                {item.label}
                <span className="absolute -bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full group-hover:left-0 group-hover:translate-x-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-32 bg-white/10 animate-pulse rounded" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-4">
      {menuItems.filter(item => item.label !== "Über Uns2").map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium uppercase tracking-wider text-white/90 transition-colors hover:text-primary" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4 pt-4 border-t border-white/10">
        <Button asChild variant="ghost" className="w-full justify-center border border-primary text-primary hover:bg-primary hover:text-white" onClick={onClose}>
          <Link to="#">KONTAKT</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent dark bg-background text-foreground", isScrolled ? "bg-background/80 backdrop-blur-xl border-white/5 shadow-lg" : "bg-background")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50">
          <img src="https://s2automation.ch/wp-content/uploads/2026/06/S2_logo_clean_HD_cropped.png" alt="S2 Automation logo" className="h-20 w-auto object-contain" style={{
          filter: "brightness(1.2)"
        }} />
        </Link>

        {}
        <div className="hidden lg:block">
          <MenuProvider menu_id="50">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:block">
          <Button asChild variant="ghost" className="border border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 px-6">
            <Link to="#">KONTAKT</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="lg:hidden text-white hover:text-primary hover:bg-white/5 z-50" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>

        {}
        <div className={cn("fixed inset-0 top-20 bg-background z-40 lg:hidden transition-all duration-300 ease-in-out overflow-y-auto", isMobileMenuOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full pointer-events-none")}>
          <MenuProvider menu_id="50">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}